import { Component } from '../../../components/Component.js';
import * as dom from '../../../core/utils/dom.js';
import { applyTranslations, getCurrentLanguage } from '../../../js/translations.js';
import { fetchCache } from '../../../js/utils/fetchCache.js';

export class FAQComponent extends Component {
  static contentCache = null;
  static loadingPromise = null;

  constructor(element) {
    super(element);
    this.currentLang = getCurrentLanguage();
  }

  init() {
    requestIdleCallback(() => this.loadFAQContent());
    document.addEventListener('languageChanged', this.handleLanguageChange);
  }

  handleLanguageChange = (e) => {
    if (!e.detail?.language) return;
    this.currentLang = e.detail.language;
    this.applyTranslations();
  };

  async loadFAQContent() {
    try {
      const faqElement = document.getElementById('faq');
      if (!faqElement) return;

      if (FAQComponent.contentCache) {
        faqElement.innerHTML = FAQComponent.contentCache;
        this.addEventListeners();
        this.applyTranslations();
        return;
      }

      if (FAQComponent.loadingPromise) {
        await FAQComponent.loadingPromise;
        if (FAQComponent.contentCache) {
          faqElement.innerHTML = FAQComponent.contentCache;
          this.addEventListeners();
          this.applyTranslations();
        }
        return;
      }

      FAQComponent.loadingPromise = this.fetchFAQContent();
      const content = await FAQComponent.loadingPromise;

      FAQComponent.contentCache = content;
      FAQComponent.loadingPromise = null;

      faqElement.innerHTML = content;
      this.addEventListeners();
      this.applyTranslations();
    } catch (error) {
      console.error('FAQ load error:', error);
      FAQComponent.loadingPromise = null;
    }
  }

  async fetchFAQContent() {
    return fetchCache.fetchText('/js/rankings/components/faq-content.html');
  }

  applyTranslations() {
    applyTranslations('rankings', this.currentLang);
  }

  toggleFAQ(question) {
    const faqItem = question.closest('.faq-item');
    const faqAnswer = faqItem.querySelector('.faq-answer');
    const isExpanded = question.getAttribute('aria-expanded') === 'true';

    question.setAttribute('aria-expanded', !isExpanded);
    faqAnswer.hidden = isExpanded;
    question.classList.toggle('is-active', !isExpanded);
  }

  addEventListeners() {
    document.querySelectorAll('.faq-container .faq-question')
      .forEach(q => q.addEventListener('click', () => this.toggleFAQ(q)));

    const viewAllButton = this.element.querySelector('.view-all-faq');
    if (viewAllButton) {
      dom.on(viewAllButton, 'click', () => this.showFAQModal());
      dom.on(viewAllButton, 'keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.showFAQModal();
        }
      });
    }
  }

  showFAQModal() {
    const faqModal = dom.$('#faqModal');
    if (!faqModal) return;

    faqModal.style.display = 'flex';
    document.body.classList.add('modal-open');

    const closeModal = () => {
      faqModal.style.display = 'none';
      document.body.classList.remove('modal-open');
    };

    this.faqModalRef = faqModal;
    this.faqModalCloseHandler = closeModal;
    this.faqModalClickHandler = (e) => e.target === faqModal && closeModal();

    const closeBtn = dom.$('.close-btn', faqModal);
    if (closeBtn) dom.on(closeBtn, 'click', this.faqModalCloseHandler);
    dom.on(faqModal, 'click', this.faqModalClickHandler);

    faqModal.querySelectorAll('.faq-question')
      .forEach(q => {
        q.addEventListener('click', () => this.toggleFAQ(q));
        q.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleFAQ(q);
          }
        });
      });

    this.applyTranslations();
  }

  destroy() {
    const faqModal = this.faqModalRef || dom.$('#faqModal');
    if (faqModal) {
      if (this.faqModalClickHandler) dom.off(faqModal, 'click', this.faqModalClickHandler);
      const closeBtn = dom.$('.close-btn', faqModal);
      if (closeBtn && this.faqModalCloseHandler) dom.off(closeBtn, 'click', this.faqModalCloseHandler);
    }
    document.removeEventListener('languageChanged', this.handleLanguageChange);
    super.destroy();
  }
}
